import { lazy, Suspense, useState, useEffect, useRef } from 'react'
import { Switch, Route, useHistory, useLocation } from 'react-router-dom'
import { Box, GlobalStyles } from '@mui/material'
import { ToastContainer } from 'react-toastify'
import { SecureRoute, Security } from '@okta/okta-react'
import { OktaAuth, toRelativeUrl } from '@okta/okta-auth-js'
import NavbarComponent from './components/navbar/navbar.component'
import OktaNavbarComponent from './components/navbar/okta-navbar.component'
import HomePageComponent from './components/homepage/home-page.component'
import TaskListComponent from './components/tasks/tasks.component'
import MyMessagesPage from './pages/MyMessagesPage'
import CorsErrorModalComponent from './components/okta/cors-error-modal.component'
import { oidcConfig, oktaAuthConfig, oktaSignInConfig } from './components/okta/config'
import SetupAxios from './utils/httpInterceptor'
import { environment } from './environments/environment'
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles'
import { appTheme } from './themes/themes'
import { ThemeProvider as AppThemeProvider, useThemeMode } from './contexts/ThemeContext'
import { lightCssVars } from './themes/tokens'
import 'react-toastify/dist/ReactToastify.css'
import './App.scss'
import { RootState, useAppThunkDispatch } from './redux/store'
import { resetLoggedInUser } from './redux/common-data-slice'
import PlanComponent from './components/patientRole/plan/plan.component'
import ActivityComponent from './components/patientRole/activity/activity.component'
import EducationComponent from './components/patientRole/education/education.component'
import MessageComponent from './components/patientRole/message/message.component'
import ErrorBoundary from './components/homepage/error-boundary.component'
import PageNotFoundComponent from './components/homepage/page-not-found.component'
import FHIRPatientCallbackComponent from './components/fhir/fhir-patient-callback.component'
import LoginCodeComponent from './components/okta/login-code.component'
import OktaLoginComponent from './components/okta/okta-login.component'
import OktaTokenRenewal from './components/okta/okta-token-renewal.component'
import SessionInactivityGuard from './components/session/SessionInactivityGuard'
import SlidebarComponent from './components/navbar/slidebar.component'
import ExpandCircleDownIcon from '@mui/icons-material/ExpandCircleDown'
import { useSelector } from 'react-redux'
import { Panel, PanelGroup } from 'react-resizable-panels'
import { AuthProvider } from 'oidc-react'
import Helper from './utils/helper'
import OIDCAuthWrapper from './oidcAuthWrapper'
import { darkModeButtonStyles } from './styles/dashboard.stylesheet'
import MiniRecorderComponent from './components/global/mini-recorder.component'
import RecordingRecoveryComponent from './components/global/recording-recovery.component'

const DetailContainerComponent = lazy(
  () => import('./components/patients/detail/detail-container.component')
)
const RecordPageComponent = lazy(
  () => import('./components/record/RecordPage').then((m) => ({ default: m.RecordPage }))
)
// Route wrapper: RecordPage takes theme mode + a toggle as props; supply them from
// the app theme context so the record view follows the global light/dark setting.
const RecordPageRoute = () => {
  const { mode } = useThemeMode()
  return <RecordPageComponent mode={mode} />
}
const QuestionnaireComponent = lazy(
  () => import('./components/questionnaires/questionnaire.component')
)
const PatientTreatmentPlanComponent = lazy(
  () => import('./components/patients/treatment-plan.component')
)
const QuestionnaireListComponent = lazy(
  () => import('./components/patients/questionnaire-list.component')
)
const ProviderManagerComponent = lazy(
  () => import('./components/providers/ProviderManager')
)
const SiteManagerComponent = lazy(
  () => import('./components/sites/SiteManager')
)
const RPMDashboardComponent = lazy(
  () => import('./components/rpm/RPMDashboard')
)
const RevOpsConsoleComponent = lazy(
  () => import('./components/rcm/RevOpsConsole')
)
const TestTrackerComponent = lazy(
  () => import('./components/rcm/TestTracker')
)
const EncountersDashboardComponent = lazy(
  () => import('./components/encounters/EncountersDashboard')
)

const authClient = new OktaAuth(oktaAuthConfig) as any

// Separate component for GlobalStyles to safely use the hook
const ThemeGlobalStyles = () => {
  const { cssVars, mode } = useThemeMode();
  const isDark = mode === 'dark';

  // Base styles — CSS vars only. Button styles are handled per-mode below.
  // Light mode: themes.tsx already defines correct outlined button style
  //   (transparent bg, orange text/border, hover = orange bg + white text)
  // Dark mode: darkModeStyles overrides to grey bg + dark text
  const baseStyles = {
    ':root': {
      ...cssVars,
      colorScheme: isDark ? 'dark' : 'light',
    },
  };

  // Light mode: themes.tsx handles active buttons (transparent bg, orange border/text, orange hover)
  // Only override disabled state — MUI default leaves disabled with faint/invisible styling
  const lightModeStyles = !isDark ? {
    '.MuiButton-outlined.Mui-disabled': {
      backgroundColor: 'var(--color-background-subtle) !important',
      color: 'var(--color-text-primary) !important',
      borderColor: 'var(--color-border-medium) !important',
      opacity: '0.5 !important',
    },
    '.MuiButton-contained.Mui-disabled': {
      backgroundColor: 'var(--color-background-subtle) !important',
      color: 'var(--color-text-primary) !important',
      opacity: '0.5 !important',
    },
  } : {};

  // Dark mode specific overrides - only applied when isDark is true
  const darkModeStyles = isDark ? {
    body: {
      color: 'var(--color-text-primary) !important',
      backgroundColor: 'var(--color-body-background) !important',
    },
    // ── Dark mode button overrides ────────────────────────────────────────────
    // Outlined: transparent bg + orange text/border (same look as light mode)
    '.MuiButton-outlined': {
      backgroundColor: 'transparent !important',
      color: 'var(--color-primary-dark) !important',
      borderColor: 'var(--color-primary-dark) !important',
    },
    // Hover: orange fill + white text (same as light mode)
    '.MuiButton-outlined:hover': {
      backgroundColor: 'var(--color-primary-dark) !important',
      color: '#FFFFFF !important',
      borderColor: 'var(--color-primary-dark) !important',
    },
    '.MuiButton-contained': {
      backgroundColor: 'var(--color-btn-primary-bg) !important',
      color: 'var(--color-btn-primary-text) !important',
    },
    // Disabled: subtle dark bg (like "Text/TTS" inactive)
    '.MuiButton-outlined.Mui-disabled': {
      backgroundColor: 'var(--color-background-subtle) !important',
      color: 'var(--color-text-primary) !important',
      borderColor: 'var(--color-border-medium) !important',
      opacity: '0.6 !important',
    },
    '.MuiButton-contained.Mui-disabled': {
      backgroundColor: 'var(--color-background-subtle) !important',
      color: 'var(--color-text-primary) !important',
      opacity: '0.6 !important',
    },
    '.MuiPaper-root': {
      backgroundColor: 'var(--color-paper) !important',
      color: 'var(--color-text-primary) !important',
    },
    '.MuiCard-root': {
      backgroundColor: 'var(--color-paper)',
      color: 'var(--color-text-primary) !important',
    },
    '.MuiCardContent-root': {
      color: 'var(--color-text-primary) !important',
    },
    '.MuiTypography-root': {
      color: 'var(--color-text-primary) !important',
    },
    '.MuiTypography-colorTextSecondary': {
      color: 'var(--color-text-secondary) !important',
    },
    '.MuiTableCell-root': {
      borderColor: 'var(--color-border-light)',
      color: 'var(--color-text-primary) !important',
      backgroundColor: 'var(--color-paper) !important',
    },
    '.MuiTableRow-root': {
      backgroundColor: 'var(--color-paper) !important',
    },
    '.MuiTableRow-root:hover': {
      backgroundColor: 'var(--color-background-subtle) !important',
    },
    '.MuiTableHead-root .MuiTableCell-root': {
      backgroundColor: 'var(--color-background-subtle) !important',
    },
    '.MuiTableBody-root': {
      backgroundColor: 'var(--color-paper) !important',
    },
    '.MuiTableContainer-root': {
      backgroundColor: 'var(--color-paper) !important',
    },
    '.MuiDataGrid-root': {
      backgroundColor: 'var(--color-paper) !important',
      color: 'var(--color-text-primary) !important',
      borderColor: 'var(--color-border-light) !important',
    },
    '.MuiDataGrid-cell': {
      borderColor: 'var(--color-border-light) !important',
      color: 'var(--color-text-primary) !important',
    },
    '.MuiDataGrid-columnHeaders': {
      backgroundColor: 'var(--color-background-subtle) !important',
      borderColor: 'var(--color-border-light) !important',
    },
    '.MuiDataGrid-columnHeader': {
      color: 'var(--color-text-primary) !important',
    },
    '.MuiDataGrid-row': {
      backgroundColor: 'var(--color-paper) !important',
    },
    '.MuiDataGrid-row:hover': {
      backgroundColor: 'var(--color-background-subtle) !important',
    },
    '.MuiDataGrid-footerContainer': {
      backgroundColor: 'var(--color-paper) !important',
      borderColor: 'var(--color-border-light) !important',
    },
    '.MuiAccordion-root': {
      backgroundColor: 'var(--color-paper) !important',
      color: 'var(--color-text-primary) !important',
    },
    '.MuiAccordionSummary-root': {
      backgroundColor: 'var(--color-background-subtle) !important',
      color: 'var(--color-text-primary) !important',
    },
    '.MuiAccordionSummary-content': {
      color: 'var(--color-text-primary) !important',
    },
    '.MuiAccordionSummary-expandIconWrapper': {
      color: 'var(--color-text-secondary) !important',
    },
    '.MuiSvgIcon-root': {
      color: 'inherit',
    },
    '.MuiSvgIcon-colorAction': {
      color: 'var(--color-text-secondary) !important',
    },
    '.MuiSvgIcon-colorDisabled': {
      color: 'var(--color-text-muted) !important',
    },
    '.MuiAccordionDetails-root': {
      backgroundColor: 'var(--color-paper) !important',
      color: 'var(--color-text-primary) !important',
    },
    '.MuiDialog-paper': {
      backgroundColor: 'var(--color-paper) !important',
      color: 'var(--color-text-primary) !important',
    },
    '.MuiDialogContent-root': {
      backgroundColor: 'var(--color-paper) !important',
      color: 'var(--color-text-primary) !important',
    },
    '.MuiDialogActions-root': {
      backgroundColor: 'var(--color-paper) !important',
    },
    '.MuiDialogTitle-root': {
      backgroundColor: 'var(--color-background-subtle) !important',
      color: 'var(--color-text-primary) !important',
    },
    '.MuiMenu-paper': {
      backgroundColor: 'var(--color-paper) !important',
    },
    '.MuiMenuItem-root': {
      color: 'var(--color-text-primary) !important',
    },
    '.MuiTooltip-tooltip': {
      backgroundColor: '#424242',
    },
    '.MuiInputBase-root': {
      color: 'var(--color-text-primary) !important',
    },
    '.MuiInputBase-input': {
      color: 'var(--color-text-primary) !important',
    },
    '.MuiOutlinedInput-notchedOutline': {
      borderColor: 'var(--color-border-medium) !important',
    },
    '.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'var(--color-text-secondary) !important',
    },
    '.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: 'var(--color-primary) !important',
    },
    '.MuiSelect-icon': {
      color: 'var(--color-text-secondary) !important',
    },
    '.MuiFormLabel-root': {
      color: 'var(--color-text-secondary) !important',
    },
    '.MuiCheckbox-root': {
      color: 'var(--color-text-secondary) !important',
    },
    '.MuiCheckbox-root.Mui-checked': {
      color: 'var(--color-primary) !important',
    },
    // color="default" checkboxes get an extra MUI class — target it explicitly
    '.MuiCheckbox-colorDefault': {
      color: 'var(--color-text-secondary) !important',
    },
    '.MuiCheckbox-colorDefault.Mui-checked': {
      color: 'var(--color-primary) !important',
    },
    // Ensure SVG icon inside checkbox always reflects the checkbox's color
    '.MuiCheckbox-root .MuiSvgIcon-root': {
      color: 'inherit !important',
    },
    '.MuiRadio-root': {
      color: 'var(--color-text-secondary) !important',
    },
    '.MuiRadio-root.Mui-checked': {
      color: 'var(--color-primary) !important',
    },
    '.MuiFormControlLabel-label': {
      color: 'var(--color-text-primary) !important',
    },
    '.MuiIconButton-root': {
      color: 'var(--color-text-secondary) !important',
    },
    '.MuiIconButton-root:hover': {
      color: 'var(--color-primary) !important',
    },
    '.MuiSlider-root': {
      color: 'var(--color-primary) !important',
    },
    '.MuiSlider-track': {
      backgroundColor: 'var(--color-primary) !important',
    },
    '.MuiSlider-rail': {
      backgroundColor: 'var(--color-border-medium) !important',
    },
    '.MuiSlider-thumb': {
      backgroundColor: 'var(--color-primary) !important',
    },
    '.MuiSlider-mark': {
      backgroundColor: 'var(--color-text-secondary) !important',
    },
    '.MuiSlider-markLabel': {
      color: 'var(--color-text-secondary) !important',
    },
    ...darkModeButtonStyles,
    '.MuiButton-root.Mui-disabled': {
      color: 'var(--color-text-muted) !important',
    },
    '.MuiTabs-indicator': {
      backgroundColor: 'var(--color-primary) !important',
    },
    '.MuiTab-root': {
      color: 'var(--color-text-secondary) !important',
    },
    '.MuiTab-root.Mui-selected': {
      color: 'var(--color-primary) !important',
    },
    '.disease-management-app': {
      backgroundColor: 'var(--color-body-background)',
      color: 'var(--color-text-primary)',
      minHeight: '100vh',
    },
    '.MuiAlert-root': {
      backgroundColor: 'var(--color-background-subtle) !important',
    },
    '.MuiAlert-standardError': {
      backgroundColor: 'rgba(211, 47, 47, 0.15) !important',
    },
    '.MuiAlert-standardWarning': {
      backgroundColor: 'rgba(237, 108, 2, 0.15) !important',
    },
    '.MuiAlert-standardInfo': {
      backgroundColor: 'rgba(2, 136, 209, 0.15) !important',
    },
    '.MuiAlert-standardSuccess': {
      backgroundColor: 'rgba(46, 125, 50, 0.15) !important',
    },
    '.MuiLinearProgress-root': {
      backgroundColor: 'var(--color-border-medium) !important',
    },
    '.MuiLinearProgress-bar': {
      backgroundColor: 'var(--color-primary) !important',
    },
    // Chip styling for dark mode
    '.MuiChip-root': {
      color: 'var(--color-text-primary) !important',
      borderColor: 'var(--color-border-medium) !important',
    },
    '.MuiChip-outlined': {
      backgroundColor: 'transparent !important',
      borderColor: 'var(--color-border-medium) !important',
      color: 'var(--color-text-primary) !important',
    },
    '.MuiChip-filled': {
      backgroundColor: 'var(--color-background-subtle) !important',
      color: 'var(--color-text-primary) !important',
    },
    '.MuiChip-deleteIcon': {
      color: 'var(--color-text-secondary) !important',
    },
    '.MuiChip-label': {
      color: 'inherit !important',
    },
    // Autocomplete styling for dark mode
    '.MuiAutocomplete-paper': {
      backgroundColor: 'var(--color-paper) !important',
    },
    '.MuiAutocomplete-option': {
      color: 'var(--color-text-primary) !important',
    },
    '.MuiAutocomplete-option[aria-selected="true"]': {
      backgroundColor: 'var(--color-background-subtle) !important',
    },
    '.MuiAutocomplete-option:hover': {
      backgroundColor: 'var(--color-background-subtle) !important',
    },
    '.MuiAutocomplete-tag': {
      color: 'var(--color-text-primary) !important',
      borderColor: 'var(--color-border-medium) !important',
    },
    '.MuiAutocomplete-inputRoot': {
      color: 'var(--color-text-primary) !important',
    },
    // Standard input underline for dark mode
    '.MuiInput-underline:before': {
      borderBottomColor: 'var(--color-border-medium) !important',
    },
    '.MuiInput-underline:hover:before': {
      borderBottomColor: 'var(--color-text-secondary) !important',
    },
    '.MuiInput-underline:after': {
      borderBottomColor: 'var(--color-primary) !important',
    },
    // MUI DatePicker calendar popup dark mode
    '.MuiPickersLayout-root': {
      backgroundColor: 'var(--color-paper) !important',
      color: 'var(--color-text-primary) !important',
    },
    '.MuiDateCalendar-root': {
      backgroundColor: 'var(--color-paper) !important',
      color: 'var(--color-text-primary) !important',
    },
    '.MuiPickersCalendarHeader-root': {
      color: 'var(--color-text-primary) !important',
    },
    '.MuiPickersCalendarHeader-label': {
      color: 'var(--color-text-primary) !important',
    },
    '.MuiPickersCalendarHeader-switchViewButton': {
      color: 'var(--color-text-secondary) !important',
    },
    '.MuiPickersArrowSwitcher-button': {
      color: 'var(--color-text-secondary) !important',
    },
    '.MuiDayCalendar-weekDayLabel': {
      color: 'var(--color-text-secondary) !important',
    },
    '.MuiPickersDay-root': {
      color: 'var(--color-text-primary) !important',
      backgroundColor: 'transparent !important',
    },
    '.MuiPickersDay-root:hover': {
      backgroundColor: 'var(--color-background-subtle) !important',
    },
    '.MuiPickersDay-root.Mui-selected': {
      backgroundColor: 'var(--color-primary) !important',
      color: '#FFFFFF !important',
    },
    '.MuiPickersDay-root.Mui-selected:hover': {
      backgroundColor: 'var(--color-primary-dark) !important',
    },
    '.MuiPickersDay-root.MuiPickersDay-today': {
      borderColor: 'var(--color-primary) !important',
    },
    '.MuiPickersDay-root.Mui-disabled': {
      color: 'var(--color-text-muted) !important',
    },
    '.MuiPickersYear-yearButton': {
      color: 'var(--color-text-primary) !important',
    },
    '.MuiPickersYear-yearButton.Mui-selected': {
      backgroundColor: 'var(--color-primary) !important',
      color: '#FFFFFF !important',
    },
    '.MuiPickersMonth-monthButton': {
      color: 'var(--color-text-primary) !important',
    },
    '.MuiPickersMonth-monthButton.Mui-selected': {
      backgroundColor: 'var(--color-primary) !important',
      color: '#FFFFFF !important',
    },
  } : {};

  return (
    <GlobalStyles
      styles={{
        ...baseStyles,
        ...lightModeStyles,
        ...darkModeStyles,
      }}
    />
  );
};

const AppComponent = () => {
  const [corsErrorModalOpen, setCorsErrorModalOpen] = useState(false)
  const [defaultMenuSize, setDefaultMenuSize] = useState(3.4)
  const history = useHistory()
  const location = useLocation()
  const onAuthRequired = () => history.push('/login/callback')
  const dispatch = useAppThunkDispatch()
  const patientDetailStatus: any = useSelector((state: RootState) => state.patientDetail.status)
  const { mode } = useThemeMode()  // For toast dark mode support
  const panelRef = useRef(null) as any

  const params = new URLSearchParams(location.search)

  useEffect(() => {
    if (!environment.okta.isEnabled) return

    const handler = (authState: any) => {
      // Skip transitional state — isPending means the SDK hasn't evaluated tokens yet.
      // Acting on this state causes spurious signInWithRedirect() calls immediately
      // after a successful login, creating a redirect loop.
      if (authState.isPending) return

      if (!authState.isAuthenticated && params.get('code') === null) {
        // Intentional logout in progress — signOut() is navigating to /login/callback
        // which LoginCodeComponent will redirect to /login. Don't race it with
        // a concurrent signInWithRedirect() PKCE flow.
        if (sessionStorage.getItem('oktaLoggingOut') === 'true') return
        // Don't trigger a new PKCE flow when already on the login or callback page
        if (window.location.pathname === '/login' || window.location.pathname === '/login/callback') return
        authClient.signInWithRedirect({ redirectUri: oktaSignInConfig.redirectUri })
      }
    }

    authClient.authStateManager.subscribe(handler)

    return () => {
      authClient.authStateManager.unsubscribe(handler)
    }
  }, [])

  useEffect(() => {
    return () => {
      dispatch(resetLoggedInUser())
    }
  }, [dispatch])

  const restoreOriginalUri = async (_oktaAuth: any, originalUri: any) => {
    history.replace(toRelativeUrl(originalUri || '/', window.location.origin))
  }

  const getOktaAccessRoute = () => {
    return (
      <Suspense fallback={<div>Loading...</div>}>
        <SetupAxios>
          <Switch>
            {/* Home/Default Routes */}
            <Route exact path="/" render={() => Helper.isAIDA()
              ? <HomePageComponent history={undefined} location={undefined} match={undefined} />
              : <TaskListComponent />}
            />

            {/* Task Routes */}
            <SecureRoute exact path="/top-tasks" component={TaskListComponent} />
            <SecureRoute exact path="/my-tasks" component={TaskListComponent} />
            <SecureRoute exact path="/my-messages" component={MyMessagesPage} />
            <SecureRoute exact path="/all-tasks" component={TaskListComponent} />

            {/* Patient Routes */}
            <SecureRoute exact path="/patient-list" component={HomePageComponent} />
            <SecureRoute exact path="/clinic-visits" component={HomePageComponent} />
            <SecureRoute
              component={DetailContainerComponent}
              exact
              path="/patients/:patientId/details"
            />
            <SecureRoute
              component={RecordPageRoute}
              exact
              path="/patients/:patientId/record"
            />
            <SecureRoute
              component={QuestionnaireListComponent}
              exact
              path="/patients/:patientId/list"
            />
            <SecureRoute
              component={PatientTreatmentPlanComponent}
              exact
              path="/patients/:patientId/treatment-plan"
            />
            <SecureRoute
              component={QuestionnaireComponent}
              exact
              path="/patients/:patientId/questionnaires/:questionnaireId"
            />

            {/* FHIR Route */}
            <SecureRoute
              exact
              path="/epic-fhir/sync/:fhirPatientId/launch-callback"
              component={FHIRPatientCallbackComponent}
            />

            {/* Provider Management Route */}
            <SecureRoute
              exact
              path="/providers"
              component={ProviderManagerComponent}
            />

            {/* Site Management Route */}
            <SecureRoute
              exact
              path="/sites"
              component={SiteManagerComponent}
            />

            {/* RPM Dashboard Route */}
            <SecureRoute
              exact
              path="/rpm-dashboard"
              component={RPMDashboardComponent}
            />

            {/* Revenue & Operations Console */}
            <SecureRoute
              exact
              path="/revops"
              component={RevOpsConsoleComponent}
            />

            {/* Test tracker — the RevOps test-lifecycle view on its own route */}
            <SecureRoute
              exact
              path="/test-tracker"
              component={TestTrackerComponent}
            />

            {/* Encounters Route */}
            <SecureRoute exact path="/encounters" component={EncountersDashboardComponent} />

            {/* Patient Role Routes */}
            <SecureRoute component={PlanComponent} exact path="/plan" />
            <SecureRoute component={ActivityComponent} exact path="/activity" />
            <SecureRoute component={EducationComponent} exact path="/education" />
            <SecureRoute component={MessageComponent} exact path="/message" />

            {/* Auth Routes */}
            <Route
              exact
              path="/login"
              render={() => (
                <OktaLoginComponent
                  history={''}
                  location={window.location}
                  match={''}
                  config={oktaSignInConfig}
                />
              )}
            />
            <Route
              path="/login/callback"
              render={() => (
                <LoginCodeComponent
                  history={''}
                  location={window.location}
                  match={''}
                  config={oktaSignInConfig}
                />
              )}
            />

            {/* 404 Route - Must be last */}
            <Route path="*" component={PageNotFoundComponent} />
          </Switch>
        </SetupAxios>
      </Suspense>
    )
  }

  const getAccessRoute = () => {
    return (
      <Suspense fallback={<div>Loading...</div>}>
        <SetupAxios>
          <Switch>
            {/* Home/Default Routes */}
            <Route exact path="/" render={() => Helper.isAIDA()
              ? <HomePageComponent history={undefined} location={undefined} match={undefined} />
              : <TaskListComponent />}
            />

            {/* Task Routes */}
            <Route exact path="/top-tasks" component={TaskListComponent} />
            <Route exact path="/my-tasks" component={TaskListComponent} />
            <Route exact path="/my-messages" component={MyMessagesPage} />
            <Route exact path="/all-tasks" component={TaskListComponent} />

            {/* Patient Routes */}
            <Route exact path="/patient-list" component={HomePageComponent} />
            <Route exact path="/clinic-visits" component={HomePageComponent} />
            <Route
              component={DetailContainerComponent}
              exact
              path="/patients/:patientId/details"
            />
            <Route
              component={RecordPageRoute}
              exact
              path="/patients/:patientId/record"
            />
            <Route
              component={QuestionnaireListComponent}
              exact
              path="/patients/:patientId/list"
            />
            <Route
              component={PatientTreatmentPlanComponent}
              exact
              path="/patients/:patientId/treatment-plan"
            />
            <Route
              component={QuestionnaireComponent}
              exact
              path="/patients/:patientId/questionnaires/:questionnaireId"
            />

            {/* Provider Management Route */}
            <Route
              exact
              path="/providers"
              component={ProviderManagerComponent}
            />

            {/* Site Management Route */}
            <Route
              exact
              path="/sites"
              component={SiteManagerComponent}
            />

            {/* RPM Dashboard Route */}
            <Route
              exact
              path="/rpm-dashboard"
              component={RPMDashboardComponent}
            />

            {/* Revenue & Operations Console */}
            <Route
              exact
              path="/revops"
              component={RevOpsConsoleComponent}
            />

            {/* Test tracker — the RevOps test-lifecycle view on its own route */}
            <Route
              exact
              path="/test-tracker"
              component={TestTrackerComponent}
            />

            {/* Encounters Route */}
            <Route exact path="/encounters" component={EncountersDashboardComponent} />

            {/* Patient Role Routes */}
            <Route component={PlanComponent} exact path="/plan" />
            <Route component={ActivityComponent} exact path="/activity" />
            <Route component={EducationComponent} exact path="/education" />
            <Route component={MessageComponent} exact path="/message" />

            {/* 404 Route - Must be last */}
            <Route path="*" component={PageNotFoundComponent} />
          </Switch>
        </SetupAxios>
      </Suspense>
    )
  }

  const getOIDCAccessRoute = () => {
    return (
      <AuthProvider {...oidcConfig} autoSignIn={false}>
        <OIDCAuthWrapper />
      </AuthProvider>
    )
  }

  useEffect(() => {
    const handleWindowResize = () => {
      if (panelRef?.current) {
        if (window.innerWidth < 1200) {
          panelRef.current.collapse()
          setDefaultMenuSize(0)
        } else {
          panelRef.current.expand()
          setDefaultMenuSize(3.4)
        }
      }
    }

    window.addEventListener('resize', handleWindowResize)
    return () => {
      window.removeEventListener('resize', handleWindowResize)
    }
  }, [])

  const tabs = ['/patient-list', '/', '/all-tasks', '/my-tasks', '/my-messages', '/top-tasks', '/clinic-visits', '/providers', '/sites', '/rpm-dashboard', '/encounters']

  const getTaskPanel = (title: any) => {
    return (
      <PanelGroup autoSaveId="example" direction="horizontal">
        {patientDetailStatus !== 'loading' && (
          <Panel
            ref={panelRef}
            collapsedSize={3.4}
            minSize={defaultMenuSize > 0 ? 3.4 : 0.5}
            maxSize={defaultMenuSize > 0 ? 3.4 : 0.5}
            style={{ overflow: 'visible' }}
          >
            <Box sx={{ position: 'relative', height: '100%', overflow: 'visible' }}>
              {defaultMenuSize > 0 && (
                <SlidebarComponent
                  defaultMenuSize={defaultMenuSize}
                  isPatient={false}
                  onToggle={() => setDefaultMenuSize(defaultMenuSize <= 0 ? 3.4 : 0)}
                />
              )}
              {defaultMenuSize === 0 && (
                <Box sx={{ borderRight: '0.12rem solid #e1e1e1', height: '100%', width: '100%' }}>
                  <ExpandCircleDownIcon
                    onClick={() => setDefaultMenuSize(3.4)}
                    sx={{
                      position: 'absolute',
                      left: tabs.includes(window.location.pathname) ? '0.15rem' : '0rem',
                      top: tabs.includes(window.location.pathname) ? '0.8rem' : '0.45rem',
                      fontSize: '1rem !important',
                      fill: 'grey !important',
                      cursor: 'pointer !important',
                      zIndex: 1000,
                      transform: 'rotate(270deg) !important',
                      '&:hover': {
                        fill: 'var(--color-primary) !important',
                      }
                    }}
                  />
                </Box>
              )}
            </Box>
          </Panel>
        )}
        <Panel style={{ borderLeft: location.pathname.endsWith('/details') ? '0.12rem solid #e1e1e1' : '' }}>
          {title === 'okta'
            ? getOktaAccessRoute()
            : getAccessRoute()
          }
        </Panel>
      </PanelGroup>
    )
  }

  return (
    <ErrorBoundary>
      <MuiThemeProvider theme={appTheme}>
        <ThemeGlobalStyles />
        <section className="disease-management-app">
          {/*
            SessionInactivityGuard wraps EVERY auth branch (CAPA F273). The HIPAA idle
            warning + countdown used to live inside OktaTokenRenewal, i.e. inside the
            Okta branch below — so the OIDC branch never had one, and because both flags
            shipped defaulting to the opposite of the pair this branch requires, stock
            configuration mounted no warning at all. A control measure must not depend on
            which auth branch is configured; each branch now registers an adapter with
            the guard instead of owning the control. Pinned by App.auth-branch.test.tsx.
          */}
          <SessionInactivityGuard>
          {environment.oidc.isEnabled
            ? <>
              {getOIDCAccessRoute()}
            </>
            : (environment.okta.isEnabled && !(environment.oidc.isEnabled)) ? (
              <Security
                oktaAuth={authClient}
                onAuthRequired={onAuthRequired}
                restoreOriginalUri={restoreOriginalUri}
              >
                <OktaTokenRenewal>
                  <OktaNavbarComponent
                    history={''}
                    location={window.location}
                    match={''}
                    {...{ setCorsErrorModalOpen }}
                  />
                  {corsErrorModalOpen && (
                    <CorsErrorModalComponent
                      history={''}
                      location={window.location}
                      match={''}
                    />
                  )}
                  {getTaskPanel('okta')}
                </OktaTokenRenewal>
              </Security>
            ) : (
              <>
                {!location.pathname.endsWith('/task-list') && (
                  <NavbarComponent
                    history={''}
                    location={window.location}
                    match={''}
                  />
                )}
                {getTaskPanel('default')}
              </>
            )}
          </SessionInactivityGuard>
          <ToastContainer theme={mode === 'dark' ? 'dark' : 'light'} />
          <MiniRecorderComponent />
          <RecordingRecoveryComponent />
        </section>
      </MuiThemeProvider>
    </ErrorBoundary>
  )
}

// Wrap with our ThemeProvider to enable theme mode switching
const AppWithTheme = () => (
  <AppThemeProvider>
    <AppComponent />
  </AppThemeProvider>
);

export default AppWithTheme